import { useEffect, useState } from "react";
import { fetchMarketNews, type MarketNewsDto } from "../api";
import { FieldHelp } from "./FieldHelp";

/** V2 market news, DISPLAY-ONLY (docs/v2/userstories_news_feed.md, narrowed
 * slice: general-news RSS only -- no NSE/BSE announcements, no ticker
 * mapping, no priced-in read, no agentic wiring). Self-contained: fetches
 * its own data so MarketPage's own fetch/state tree is untouched.
 *
 * No score/sentiment is ever rendered here, by design -- the API response
 * carries no such field for this to display even if a future edit tried to.
 */

function fmtAge(minutes: number | null): string {
  if (minutes === null) return "unknown";
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h ago`;
  return `${(hours / 24).toFixed(1)}d ago`;
}

function fmtPublished(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMin = (now.getTime() - d.getTime()) / 60000;
  if (diffMin < 60) return `${Math.max(0, Math.round(diffMin))}m ago`;
  if (diffMin < 24 * 60) return `${(diffMin / 60).toFixed(1)}h ago`;
  return d.toLocaleDateString();
}

export function MarketNewsPanel({ token }: { token: string }) {
  const [data, setData] = useState<MarketNewsDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchMarketNews(token)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [token]);

  const loudFeeds = data?.feed_status.filter((f) => f.status !== "fresh") ?? [];

  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>
          Market news <span className="note">headlines only — never fed to any model or prediction</span>
          <FieldHelp term="market_news_display_only" />
        </span>
      </div>

      {err && <div className="empty-state" style={{ padding: 20 }}>Could not load market news: {err}</div>}

      {!err && !data && <div className="empty-state" style={{ padding: 20 }}>Loading…</div>}

      {data && (
        <>
          {loudFeeds.length > 0 && (
            <div className="news-feed-warning">
              {loudFeeds.map((f) => (
                <div key={f.source}>
                  ⚠ <b>{f.display_name}</b> is {f.status === "stale" ? "stale" : "unreachable"}
                  {f.newest_item_age_minutes !== null && f.status === "stale"
                    ? ` — newest item ${fmtAge(f.newest_item_age_minutes)}`
                    : ""}
                  . Showing headlines from the other feeds only.
                </div>
              ))}
            </div>
          )}

          {data.items.length === 0 ? (
            <div className="empty-state" style={{ padding: 20 }}>
              No headlines captured yet — the feed poller runs on its own cadence.
            </div>
          ) : (
            <div className="news-feed-list">
              {data.items.map((item) => (
                <a
                  key={item.item_id}
                  className="news-feed-item"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="news-feed-item-title">{item.title}</div>
                  {item.summary && (
                    <div className="news-feed-item-summary">{item.summary}</div>
                  )}
                  <div className="news-feed-item-meta">
                    <span className="news-feed-item-source">{item.source.replace(/_/g, " ")}</span>
                    <span>{fmtPublished(item.published_at)}</span>
                    {!item.summary && <span className="news-feed-item-rawnote">headline only — no summary yet</span>}
                  </div>
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
